import React, { useMemo } from 'react';
import { CategoryConfigurationDefinitionResponse, ConfigurationFieldType } from '@ydistri/api-sdk';
import { styled } from 'styled-components';
import { isEntityListConfiguration } from './segmentPotentialDefinitionLib';
import RequestListConfigurationInfo from './RequestListConfigurationInfo';
import { InfoIconTooltip } from '../../../../components/icons/InfoIconTooltip';
import { IconButton_IconSizes, Text } from '@ydistri/ds';
import { isSectionTypeTarget } from '../configurationUtils';
import { EntityListInfo } from '../../../ProjectAdministration/EntityLists/entityListsTypes';
import EntityListDeleteButton from './ConfigurationEntityLists/EntityListDeleteButton';
import SpecialOptionDeleteButton, {
  SpecialConfigurationSettings,
} from './ConfigurationEntityLists/SpecialOptionDeleteButton';
import EntityListScopeDisableButton from './ConfigurationEntityLists/EntityListScopeDisableButton';
import {
  entityListScopeExcludedConfigurationPairs,
  isScopeConfigurationWithExcludedVariant,
} from './ConfigurationEntityLists/entityListLib';
import { EntityListDescriptionInfoIconTooltip } from '../../../ProjectAdministration/common/EntityListDescription';
import EntityListSourceTypeIcon from '../../../../components/domain/EntityLists/EntityListSourceTypeIcon';
import ArchivedEntityListIcon from '../../../../components/domain/EntityLists/ArchivedEntityListIcon';
import DataDeletedEntityListIcon from '../../../../components/domain/EntityLists/DataDeletedEntityListIcon';
import DefaultEntityListIcon from '../../../../components/domain/EntityLists/DefaultEntityListIcon';
import EntityListTypeBadge from '../../../../components/domain/EntityLists/EntityListTypeBadge';
import { COLORS, computeRemSize } from '@ydistri/theme';

interface ConfigurationTitleWrapperProps {
  $isTarget: boolean;
}

export const ConfigurationTitleWrapper = styled.div<ConfigurationTitleWrapperProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  width: ${p => computeRemSize(p.$isTarget ? 200 : 300)};
  min-width: ${p => computeRemSize(p.$isTarget ? 200 : 300)};
`;

export const Gap = styled.div`
  width: 24px;
`;

export const ConfigurationTitle = styled.div<{
  $isEntityList?: boolean;
  $isArchived?: boolean;
  $isHighlighted?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: ${p => (p.$isArchived ? COLORS.GREY : 'inherit')};
  ${p => p.$isEntityList && `width: 100%;`}
  ${p => p.$isHighlighted && `font-weight: 500; font-size: 2rem;`}
`;

interface ConfigurationRowTitleProps {
  definition: CategoryConfigurationDefinitionResponse;
  forcedTitle?: string;
  entityListInfo?: EntityListInfo;
  specialSkuListInfo?: SpecialConfigurationSettings; //if true and fully featured, delete button for this config will be shown
  fullyFeatured?: boolean;
  highlighted?: boolean;
  disabled?: boolean;
}

const ConfigurationRowTitle: React.FC<ConfigurationRowTitleProps> = ({
  definition,
  forcedTitle,
  entityListInfo,
  specialSkuListInfo,
  fullyFeatured = true,
  highlighted = false,
  disabled = false,
}) => {
  const { dataType, fieldType, title, description, sectionType } = definition;

  const displayTitle = forcedTitle ?? title;

  const additionalInfo: React.ReactNode = useMemo(() => {
    if (fieldType === ConfigurationFieldType.PtRequestList) return <RequestListConfigurationInfo />;
    if (
      entityListInfo?.entityList &&
      isEntityListConfiguration(fieldType, dataType) &&
      fullyFeatured
    ) {
      // return <EntityListConfigurationInfo entityList={entityListInfo.entityList} />;
    }
    if (entityListInfo && fullyFeatured && isScopeConfigurationWithExcludedVariant(fieldType)) {
      return (
        <EntityListScopeDisableButton
          categoryId={entityListInfo.categoryId}
          fieldType={entityListScopeExcludedConfigurationPairs[fieldType]}
          entityListId={entityListInfo.entityList.entityListId}
          disabled={disabled}
        />
      );
    }
    return null;
  }, [fieldType, entityListInfo, dataType, fullyFeatured, disabled]);

  const isTarget = isSectionTypeTarget(sectionType);

  const tooltipIcon = useMemo(() => {
    if (entityListInfo?.entityList.description) {
      return (
        <EntityListDescriptionInfoIconTooltip description={entityListInfo.entityList.description} />
      );
    }

    if (!forcedTitle && description) {
      return <InfoIconTooltip htmlContent={description} />;
    }
  }, [description, entityListInfo, forcedTitle]);

  return (
    <ConfigurationTitleWrapper $isTarget={isTarget}>
      <ConfigurationTitle
        $isArchived={entityListInfo?.entityList.isArchived}
        $isEntityList={!!entityListInfo}
        $isHighlighted={highlighted}
      >
        {entityListInfo && fullyFeatured && (
          <EntityListDeleteButton
            entityListInfo={entityListInfo}
            configurationDefinition={definition}
            disabled={disabled}
          />
        )}
        {specialSkuListInfo?.displayDeleteButton &&
          fullyFeatured &&
          (specialSkuListInfo.displayDeleteButton === 'gap' ? (
            <Gap> </Gap>
          ) : (
            <SpecialOptionDeleteButton
              categoryId={specialSkuListInfo.categoryId}
              configurationDefinition={definition}
              disabled={disabled}
            />
          ))}
        <Text $size={highlighted ? 'xLarge' : undefined} $italic={specialSkuListInfo?.italic}>
          {displayTitle}
        </Text>
        {entityListInfo && (
          <>
            {entityListInfo.entityList.isDefault && <DefaultEntityListIcon />}
            <EntityListSourceTypeIcon
              entityListSourceType={entityListInfo.entityList.entityListSourceTypeId}
              size={IconButton_IconSizes.xSmall}
              entityListId={entityListInfo.entityList.entityListId}
              entityListVersionId={
                entityListInfo.configuredVersion
                  ? entityListInfo.configuredVersion.entityListVersionId
                  : entityListInfo.entityList.lastEntityListVersionId
              }
              customerEntityListId={entityListInfo.entityList.customerEntityListId}
            />
            <EntityListTypeBadge
              typeNames={entityListInfo.entityList.entityListTypes.map(
                elt => elt.entityListTypeName,
              )}
            />
          </>
        )}
        {entityListInfo?.entityList.isDataDeleted ? (
          <DataDeletedEntityListIcon />
        ) : (
          <>
            {entityListInfo?.entityList.isArchived && <ArchivedEntityListIcon />}
            {entityListInfo?.configuredVersion?.isDataDeleted && (
              <DataDeletedEntityListIcon of="version" />
            )}
          </>
        )}
        {tooltipIcon}
      </ConfigurationTitle>
      {additionalInfo}
    </ConfigurationTitleWrapper>
  );
};

export default ConfigurationRowTitle;
